import { Inject, OnDestroy, OnInit, Input, Directive } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IWindowSnare } from '../../models/interfaces/i-window-snare.interface';
import { Theme } from '../../models/themes/theme.model';
import { WINDOW } from '../../shared-providers/window.providers';
import { SessionStorageService } from '../../shared-services/services/session-storage.service';
import { ThemeService } from '../../shared-services/services/theme.service';

@Directive()
export abstract class MarginalDirective implements OnInit, OnDestroy {

    @Input() assetVerificationId: string;
    @Input() invitationLinkKey: string;
    @Input() invitationKey: string;

    unsubscriber: Subject<any> = new Subject();
    public theme: Theme;

    public abstract themeAvailable(): void;

    constructor(
        protected themeService: ThemeService,
        protected sessionStorageService: SessionStorageService,
        @Inject(WINDOW) protected window: IWindowSnare) {
    }

    ngOnInit(): void {

        this.theme = this.themeService.getTheme();
        this.prepareTheme();
    }

    ngOnDestroy(): void {
        this.unsubscriber.next(null);
        this.unsubscriber.complete();
    }

    protected prepareTheme(): void {

        if (this.theme) {
            this.themeAvailable();

        } else if (this.assetVerificationId) {
            this.loadThemeForAssetVerification();

        } else if (this.invitationKey) {
            this.loadThemeForInvitation();

        } else if (this.invitationLinkKey) {
            this.loadThemeForInvitationLink();

        } else if (!this.sessionStorageService.getAuthToken()) {
            // If not logged in load theme associated with hostname
            this.loadThemeForHost();

        } else {
            // If logged in load theme pull from api
            this.loadTheme();
        }
    }

    protected loadTheme(): void {
        this.themeService.loadTheme()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(theme => {
                this.theme = theme;
                this.themeAvailable();
            });
    }

    protected loadThemeForHost(): void {
        const sub = this.themeService.loadThemeForHost()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(theme => {
                this.theme = theme;
                this.themeAvailable();
                if (sub) {
                    sub.unsubscribe();
                }
            });
    }

    protected loadThemeForAssetVerification(): void {
        this.themeService.loadThemeForAssetVerificationPage(this.assetVerificationId)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(theme => {
                this.theme = theme;
                this.themeAvailable();
            });
    }

    protected loadThemeForInvitationLink(): void {
        this.themeService.loadThemeForInvitationLink(this.invitationLinkKey)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(theme => {
                this.theme = theme;
                this.themeAvailable();
            });
    }

    protected loadThemeForInvitation(): void {
        this.themeService.loadThemeForInvitation(this.invitationKey)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(theme => {
                this.theme = theme;
                this.themeAvailable();
            });
    }
}
